/**
 * Intervalos de retorno — a tela onde a loja manda no prazo.
 *
 * Nada aqui é decidido pelo sistema. O número entra como proposta, e só passa a
 * valer quando alguém da oficina assina dizendo de onde tirou. Mudou o número,
 * a assinatura cai: quem assinou 100 mil não assinou 60 mil.
 */
import { useState } from 'react';
import { useResource } from '../../lib/useResource.js';
import { post, patch } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { Alert, Badge, Card, Empty, ErrorBox, Field, Modal, useSubmit, useToast } from '../../components/ui.js';
import { useSession } from '../../lib/session.js';

interface Regra {
  id: string;
  purpose: string;
  categoria: string | null;
  intervaloMeses: number | null;
  intervaloKm: number | null;
  aprovada: boolean;
  fonte: string | null;
  responsavel: string | null;
  atualizadaEm: string;
}

/** Nome de gente para a finalidade técnica. */
const ROTULO: Record<string, string> = {
  oil_change: 'Troca de óleo do motor',
  atf_change: 'Troca de fluido do câmbio automático',
  manual_gearbox_oil_change: 'Troca de óleo do câmbio manual',
  differential_oil_change: 'Troca de óleo do diferencial',
  drive_axle_oil_change: 'Troca de óleo da tração',
  transfer_case_oil_change: 'Troca de óleo da caixa de transferência',
  lubrication: 'Lubrificação',
};

function descreverIntervalo(r: Regra): string {
  const partes: string[] = [];
  if (r.intervaloKm) partes.push(`${r.intervaloKm.toLocaleString('pt-BR')} km`);
  if (r.intervaloMeses) partes.push(`${r.intervaloMeses} ${r.intervaloMeses === 1 ? 'mês' : 'meses'}`);
  // "ou" porque vale o que vier primeiro.
  return partes.join(' ou ') || '—';
}

export function Intervalos() {
  const { can } = useSession();
  const toast = useToast();
  const { data, error, reload, loading } = useResource<{ regras: Regra[] }>('/api/maintenance-rules');
  const [editando, setEditando] = useState<Regra | null>(null);
  const [assinando, setAssinando] = useState<Regra | null>(null);

  const podeMexer = can('reminders.write');
  const regras = data?.regras ?? [];
  const aprovadas = regras.filter((r) => r.aprovada).length;

  return (
    <>
      <ErrorBox error={error} />

      {regras.length > 0 && aprovadas === 0 ? (
        <Alert tone="warning" title="Nenhum intervalo aprovado ainda">
          Enquanto ninguém assinar, a mensagem de retorno <strong>pergunta</strong> a quilometragem
          em vez de afirmar que está na hora. É de propósito: intervalo chutado manda o cliente à
          oficina cedo demais, ou tarde demais.
        </Alert>
      ) : null}

      <Card
        title="Intervalos de retorno"
        description="Depois de cada serviço, em quanto tempo o cliente deve voltar. Quem decide é a oficina."
        actions={
          <button type="button" onClick={reload} disabled={loading}>
            Recarregar
          </button>
        }
      >
        {regras.length === 0 ? (
          <Empty title="Nenhum intervalo cadastrado." />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Serviço</th>
                  <th>Retorno em</th>
                  <th>Situação</th>
                  <th>Fonte / quem assinou</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {regras.map((r) => (
                  <tr key={r.id}>
                    <td>{ROTULO[r.purpose] ?? r.purpose}</td>
                    <td>{descreverIntervalo(r)}</td>
                    <td>
                      {r.aprovada ? (
                        <Badge tone="neutral">em uso</Badge>
                      ) : (
                        <Badge tone="attention">proposta</Badge>
                      )}
                    </td>
                    <td className="muted small">
                      {r.fonte ? (
                        <>
                          {r.fonte}
                          {r.responsavel ? <> — {r.responsavel}</> : null}
                          <div>{formatDate(r.atualizadaEm)}</div>
                        </>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="num">
                      {podeMexer ? (
                        <>
                          <button type="button" className="small" onClick={() => setEditando(r)}>
                            Mudar prazo
                          </button>{' '}
                          {!r.aprovada ? (
                            <button type="button" className="small button-primary" onClick={() => setAssinando(r)}>
                              Aprovar
                            </button>
                          ) : null}
                        </>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="hint">
          Mudar o prazo derruba a aprovação: o número novo precisa de assinatura nova. Vale o que
          vencer primeiro, quilometragem ou tempo.
        </p>
      </Card>

      {editando ? (
        <ModalPrazo
          regra={editando}
          onClose={() => setEditando(null)}
          onSalvo={() => {
            setEditando(null);
            toast('Prazo alterado. Precisa ser aprovado de novo para voltar a valer.');
            reload();
          }}
        />
      ) : null}

      {assinando ? (
        <ModalAprovar
          regra={assinando}
          onClose={() => setAssinando(null)}
          onSalvo={() => {
            setAssinando(null);
            toast('Intervalo aprovado e em uso.');
            reload();
          }}
        />
      ) : null}
    </>
  );
}

function ModalPrazo({ regra, onClose, onSalvo }: { regra: Regra; onClose: () => void; onSalvo: () => void }) {
  const [km, setKm] = useState(regra.intervaloKm ? String(regra.intervaloKm) : '');
  const [meses, setMeses] = useState(regra.intervaloMeses ? String(regra.intervaloMeses) : '');
  const [motivo, setMotivo] = useState('');

  const salvar = useSubmit(async () => {
    await patch(`/api/maintenance-rules/${regra.id}`, {
      intervaloKm: km.trim() ? Number(km) : null,
      intervaloMeses: meses.trim() ? Number(meses) : null,
      motivo,
    });
    onSalvo();
  });

  return (
    <Modal
      title={`Prazo — ${ROTULO[regra.purpose] ?? regra.purpose}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose}>Cancelar</button>
          <button
            type="button"
            className="button-primary"
            disabled={salvar.saving || motivo.trim().length < 3 || (!km.trim() && !meses.trim())}
            onClick={() => void salvar.submit()}
          >
            {salvar.saving ? 'Salvando…' : 'Salvar prazo'}
          </button>
        </>
      }
    >
      <p className="muted">
        Deixe um em branco se só um critério importa. Com os dois preenchidos, vale o que vencer
        primeiro.
      </p>
      <div className="grid cols-2">
        <Field label="Quilômetros" hint="Ex.: 10000">
          <input inputMode="numeric" value={km} onChange={(e) => setKm(e.target.value.replace(/\D/g, ''))} />
        </Field>
        <Field label="Meses" hint="Ex.: 12">
          <input inputMode="numeric" value={meses} onChange={(e) => setMeses(e.target.value.replace(/\D/g, ''))} />
        </Field>
      </div>
      <Field label="Por que está mudando?" required hint="Fica na auditoria.">
        <input value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ex.: uso severo na região, estrada de terra." />
      </Field>
      <Alert tone="info" title="Isto tira o intervalo de uso até ser aprovado de novo">
        Quem assinou o número antigo não assinou o novo.
      </Alert>
      <ErrorBox error={salvar.error} />
    </Modal>
  );
}

function ModalAprovar({ regra, onClose, onSalvo }: { regra: Regra; onClose: () => void; onSalvo: () => void }) {
  const [fonte, setFonte] = useState(regra.fonte ?? '');
  const [responsavel, setResponsavel] = useState(regra.responsavel ?? '');

  const salvar = useSubmit(async () => {
    await post(`/api/maintenance-rules/${regra.id}/aprovar`, { fonte, responsavel });
    onSalvo();
  });

  return (
    <Modal
      title={`Aprovar — ${ROTULO[regra.purpose] ?? regra.purpose}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose}>Cancelar</button>
          <button
            type="button"
            className="button-primary"
            disabled={salvar.saving || fonte.trim().length < 3 || responsavel.trim().length < 2}
            onClick={() => void salvar.submit()}
          >
            {salvar.saving ? 'Aprovando…' : 'Aprovar e pôr em uso'}
          </button>
        </>
      }
    >
      <p>
        Retorno em <strong>{descreverIntervalo(regra)}</strong>. A partir da aprovação, a mensagem
        de retorno pode citar este prazo.
      </p>
      <Field label="De onde veio este intervalo?" required hint="Manual do fabricante, decisão técnica da oficina, referência de mercado…">
        <input value={fonte} onChange={(e) => setFonte(e.target.value)} />
      </Field>
      <Field label="Quem está assinando?" required hint="Nome de quem responde por este número.">
        <input value={responsavel} onChange={(e) => setResponsavel(e.target.value)} placeholder="Ex.: Gil (proprietário)" />
      </Field>
      <ErrorBox error={salvar.error} />
    </Modal>
  );
}
